<template>
  <div class="engagement-over-time-section">
    <h3 class="section-title">Patient Engagement Over Time</h3>
    <div class="chart-card">
      <p class="chart-subtitle">* Engagement Rate for Practice View</p>
      <div class="chart-container">
        <canvas ref="chartCanvas"></canvas>
        <div ref="tooltipEl" class="custom-tooltip"></div>
        <div v-if="!hasData" class="chart-empty">No engagement data available.</div>
      </div>
      <div class="chart-legend">
        <div class="legend-item">
          <div class="legend-dot engaged"></div>
          <span class="legend-label">Engaged</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot not-engaged"></div>
          <span class="legend-label">Not Engaged</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Chart, registerables } from 'chart.js';
import { processEngagementData, CHART_COLORS } from '../utils/chartHelpers';

Chart.register(...registerables);

const store = useStore();
const chartCanvas = ref(null);
const tooltipEl = ref(null);
let chartInstance = null;

const series = computed(() => store.getters['myPractice/clinicDashboard/patientEngagementChartData']);
const hasData = computed(() => series.value && series.value.length > 0);

// Custom external tooltip handler
const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const tooltipElement = tooltipEl.value;
  
  if (!tooltipElement) return;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipElement.style.opacity = '0';
    return;
  }

  // Set content
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const dataIndex = tooltip.dataPoints[0].dataIndex;
    const engagedValue = tooltip.dataPoints.find(dp => dp.dataset.label === 'Engaged')?.parsed.y || 0;
    const notEngagedValue = tooltip.dataPoints.find(dp => dp.dataset.label === 'Not Engaged')?.parsed.y || 0;
    const totalActivities = engagedValue + notEngagedValue;

    let innerHtml = '<div class="tooltip-content">';
    
    // Title (Week N)
    titleLines.forEach(title => {
      innerHtml += `<p class="tooltip-title">${title}</p>`;
    });

    // Total Engagement Activities
    innerHtml += `<div class="tooltip-row">`;
    innerHtml += `<p class="tooltip-label">Total Engagement Activities:</p>`;
    innerHtml += `<p class="tooltip-value">${totalActivities}</p>`;
    innerHtml += `</div>`;

    // Engaged (Provider replied)
    innerHtml += `<div class="tooltip-row">`;
    innerHtml += `<div class="tooltip-dot engaged"></div>`;
    innerHtml += `<p class="tooltip-label">Engaged (Provider replied):</p>`;
    innerHtml += `<p class="tooltip-value">${engagedValue}</p>`;
    innerHtml += `</div>`;

    // Not Engaged (No reply)
    innerHtml += `<div class="tooltip-row">`;
    innerHtml += `<div class="tooltip-dot not-engaged"></div>`;
    innerHtml += `<p class="tooltip-label">Not Engaged (No reply):</p>`;
    innerHtml += `<p class="tooltip-value">${notEngagedValue}</p>`;
    innerHtml += `</div>`;

    innerHtml += '</div>';
    tooltipElement.innerHTML = innerHtml;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Position tooltip above the point
  tooltipElement.style.opacity = '1';
  tooltipElement.style.left = positionX + tooltip.caretX + 'px';
  tooltipElement.style.top = positionY + tooltip.caretY + 'px';
  tooltipElement.style.transform = 'translate(-50%, -100%)';
};

function renderChart() {
  if (!chartCanvas.value || !hasData.value) return;
  if (chartInstance) chartInstance.destroy();

  const { labels, engagedData, notEngagedData, yAxisConfig } = processEngagementData(series.value);
  console.log('labels', labels);
  console.log('engagedData', engagedData);
  console.log('notEngagedData', notEngagedData);
  console.log('yAxisConfig', yAxisConfig);

  const customXAxisLabelsPlugin = {
    id: 'customXAxisLabels',

    afterDraw(chart) {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;

      if (!xScale || !yScale) return;

      // 让 label 出现在最小负值位置再往下 5px
      const stepSize = yScale.options.ticks.stepSize;
      const labelY = yScale.min === 0 ? yScale.getPixelForValue(yScale.min) + 11 : yScale.getPixelForValue(yScale.min + Math.ceil(stepSize / 2));

      // 竖线 tick 的顶部和底部
      const tickTopY = yScale.getPixelForValue(0);   // 从 0 轴开始
      const tickBottomY = labelY - 8;                // 到 label 上方一点

      ctx.save();

      // 画竖线 tick
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 1;

      // 画 label
      ctx.fillStyle = '#666';
      ctx.font = '12px Inter 400';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      xScale.ticks.forEach((tick, i) => {
        const x = xScale.getPixelForTick(i);

        // 竖线 tick
        ctx.beginPath();
        ctx.moveTo(x, tickTopY);
        ctx.lineTo(x, tickBottomY);
        ctx.stroke();

        // label
        ctx.fillText(String(tick.label), x, labelY);
      });

      ctx.restore();
    }
  };

  chartInstance = new Chart(chartCanvas.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Engaged',
          data: engagedData,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const active = chart.getActiveElements();

            if (active.length > 0) {
              const hoverIndex = active[0].index;

              if (ctx.dataIndex === hoverIndex) {
                return CHART_COLORS.engaged.hover;
              }
            }

            return CHART_COLORS.engaged.base;
          },
          hoverBackgroundColor: (ctx) => {
            const chart = ctx.chart;
            const active = chart.getActiveElements();

            if (active.length > 0) {
              const hoverIndex = active[0].index;

              if (ctx.dataIndex === hoverIndex) {
                return CHART_COLORS.engaged.hover;
              }
            }

            return CHART_COLORS.engaged.base;
          },
          borderRadius: { topLeft: 5, topRight: 5 },
          barPercentage: 0.9,
          categoryPercentage: 0.6,
        },
        {
          label: 'Not Engaged',
          data: notEngagedData,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const active = chart.getActiveElements();

            if (active.length > 0) {
              const hoverIndex = active[0].index;

              if (ctx.dataIndex === hoverIndex) {
                return CHART_COLORS.notEngaged.hover;
              }
            }

            return CHART_COLORS.notEngaged.base;
          },
          hoverBackgroundColor: (ctx) => {
            const chart = ctx.chart;
            const active = chart.getActiveElements();

            if (active.length > 0) {
              const hoverIndex = active[0].index;

              if (ctx.dataIndex === hoverIndex) {
                return CHART_COLORS.notEngaged.hover;
              }
            }

            return CHART_COLORS.notEngaged.base;
          },
          borderRadius: { topLeft: 5, topRight: 5 },
          barPercentage: 0.9,
          categoryPercentage: 0.6,
        },
      ],
    },
    plugins: [customXAxisLabelsPlugin],
    options: {
      layout: {
        padding: {
          bottom: 40,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      interaction: { 
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: false,
          grid: { 
            color: 'transparent',
            tickColor: '#666',
            drawTicks: true,
            offset: false,
          },
          ticks: { 
            display: false
          },
          border: {
            color: '#999',
            width: 1,
          },
          tickColor: '#666',
        },
        y: {
          stacked: false,
          min: yAxisConfig.min,
          max: yAxisConfig.max,
          ticks: {
            stepSize: yAxisConfig.stepSize,
            color: '#666',
            font: { size: 12, family: 'Inter', weight: '300' },
          },
          grid: { 
            tickColor: '#666',
            color: '#E0E0E0',
            borderDash: [5, 5],
          },
          border: { 
            color: '#999', 
            width: 1,
            dash: [5, 5]
          },
        },
      },
    },
  });
}

watch(series, () => nextTick(renderChart), { deep: true });
onMounted(() => nextTick(renderChart));
onUnmounted(() => { if (chartInstance) chartInstance.destroy(); });
</script>

<style scoped>
.engagement-over-time-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0;
  width: 100%;
}

.chart-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 24px;
  flex: 1;
}

.chart-subtitle {
  font-family: 'Inter', sans-serif !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666;
  margin: 0;
  text-align: center;
}

.chart-container {
  position: relative;
  min-height: 396px;
  flex: 1;
  width: 100%;
}

.chart-container .custom-tooltip {
  position: absolute;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1000;
  background: transparent !important;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 14px;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-dot.engaged {
  background: #25B8C2;
}

.legend-dot.not-engaged {
  background: #E0E0E0;
}

.legend-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  text-align: center;
}
</style>

<style>
.chart-container .tooltip-content {
  background: #F9FBFD;
  border-radius: 5px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.3);
  padding: 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: nowrap;
}

.chart-container .tooltip-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #666;
  margin: 0;
}

.chart-container .tooltip-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.chart-container .tooltip-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  margin: 0;
}

.chart-container .tooltip-value {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  margin: 0;
}

.chart-container .tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chart-container .tooltip-dot.engaged {
  background: #25B8C2;
}

.chart-container .tooltip-dot.not-engaged {
  background: #E0E0E0;
}
</style>
